const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
// `Z`, `+00:00` and `+0000` are all the same zero offset.
const ZERO_OFFSET_PATTERN = /(?:Z|[+-]00:?00)$/i;

/**
 * Parses a date string as a calendar date in the local timezone.
 *
 * Date-only values (`YYYY-MM-DD`) from checkout/PO are calendar dates, not UTC
 * instants — `new Date("2026-01-14")` is UTC midnight and can display as the
 * previous day in western timezones.
 *
 * Datetime strings at UTC midnight (e.g. `2026-04-15T00:00:00Z`, and the same
 * instant written `+00:00`/`+0000`) are treated the same way, using the UTC date
 * parts as the calendar date.
 */
export function parseCalendarDate(dateString: string): Date | null {
  if (typeof dateString !== "string") return null;
  const trimmed = dateString.trim();
  const dateOnly = DATE_ONLY_PATTERN.exec(trimmed);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]);
    const day = Number(dateOnly[3]);
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }
    return date;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  if (
    ZERO_OFFSET_PATTERN.test(trimmed) &&
    parsed.getUTCHours() === 0 &&
    parsed.getUTCMinutes() === 0 &&
    parsed.getUTCSeconds() === 0 &&
    parsed.getUTCMilliseconds() === 0
  ) {
    return new Date(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
    );
  }

  return parsed;
}

export function formatCalendarDate(
  dateString: string,
  locale: string = "en-US",
): string {
  try {
    const date = parseCalendarDate(dateString);
    if (!date) {
      return dateString;
    }

    const normalizedLocale = locale.replace("_", "-");
    return new Intl.DateTimeFormat(`${normalizedLocale}-u-ca-gregory`, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  } catch {
    return dateString;
  }
}

/**
 * Whole days from a calendar date up to today, counted on the calendar.
 *
 * Counts calendar days rather than elapsed milliseconds: local midnights are 23
 * or 25 hours apart across a DST transition, so dividing elapsed time by 24h
 * loses (or gains) a day in timezones that change their clocks.
 *
 * Returns null when the date cannot be parsed, and a negative number for dates
 * in the future.
 */
export function calendarDaysSince(dateString: string): number | null {
  const date = parseCalendarDate(dateString);
  if (!date) return null;

  const dayNumber = (d: Date): number =>
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());

  return Math.round((dayNumber(new Date()) - dayNumber(date)) / 86_400_000);
}

export const timePassed = (date: Date): boolean => date.getTime() < Date.now();

export const addSeconds = (date: Date, seconds: number): Date =>
  new Date(date.getTime() + seconds * 1000);
